/**
 * SignificantChangeService — V96-3-6 / ADR-0045 §2.
 *
 * FedRAMP CR26 Significant Change Notifications:
 *   - classify a change routine / adaptive / transformative from the SCN
 *     question set (data/scn-questions.ts, each question cites its rule),
 *     following the SCN-CSO-EVA evaluation order. A certification class
 *     change is refused: it needs a new assessment, not an SCN.
 *   - compute the notice obligations for the class. Anchors and direction
 *     (before start / after completion / after verification) are data in
 *     SCN_NOTICE_RULES; the windows come from `certification_timeframes`
 *     via lookupTimeframe — never code. A missing timeframe marks that
 *     notice `blocked_rules_not_approved` instead of inventing a number.
 *   - due dates stay null until their anchor happens; `transition` records
 *     completion / verification and recomputes.
 *
 * Tenant rows (`significant_changes`) live on `db`; the reviewed rule
 * content (`certification_timeframes`) on `contentDb` when the workspace
 * runs on a separate data plane (ADR-0018).
 *
 * V98-5-3 / D-98-13 (migration 235) — a regulator-facing artifact:
 *   - the computed class is a SUGGESTION (`suggested_class`, with the
 *     questions and rules that produced it); a person DECIDES the class
 *     (`decideClassification`: who, when, why). Notices are computed from the
 *     decided class once there is one (else the suggestion, provisionally).
 *   - AI model / provider changes (`ai_model_change`) need a co-approver for
 *     any decision short of transformative: a signature request of kind
 *     'scn' routed by the SoD engine (the classifier is never eligible —
 *     pair `scn_classifier_co_approver`). The assessor's view is recorded as
 *     ADVISORY (`recordAssessorView`) and never satisfies the co-approval.
 *   - the notice draft is GENERATED FROM THE BOUNDARY (systems' facts, the
 *     external-services register, the program's agencies); the author may
 *     edit the narrative and the claims, and every read re-checks the draft
 *     against the current boundary. Send is blocked while any contradiction
 *     stands (e.g. a provider the draft calls "listed" that the register
 *     does not have).
 *   - Send records the ACTUAL sender (the session user) and needs an explicit
 *     confirm of the exact draft (its sha256): one append-only
 *     `scn_notice_sends` row per notice.
 *   - a transformative decision files ONE "engage the assessor" work item
 *     and ONE `significant_change` finding (dedup `scn-trf:<change>`), which
 *     closes when the engagement is recorded done.
 */
import { programsWhere } from '../programs/manifests.js';
import { SCN_NOTICE_RULES, SCN_QUESTIONS, SCN_QUESTION_SET_VERSION, } from '../data/scn-questions.js';
import { addTimeframe, getProfile, lookupTimeframe, subtractTimeframe, timeframeProvenance, } from './certification-service.js';
import { generateRandomId } from '../utils/audit-crypto.js';
import { canonicalJson, sha256Hex } from './certification-package-service.js';
import { currentBoundarySystemIds, listExternalServices, listSystemFacts, normalizeProviderKey } from './boundary-service.js';
import { hasAssignment } from './assignment-service.js';
import { activeMemberIds } from './data-plane-router.js';
import { createSignatureRequest, getSignatureRequest, markObsoleteForArtifact, sourceCanonicalJson, sourceSha256, } from './signature-request-service.js';
import { closeWorkItem, createWorkItem, WorkItemDuplicateError } from './work-item-service.js';
import { sodEventStatement } from './sod-service.js';
export class SignificantChangeValidationError extends Error {
    constructor(message) {
        super(message);
        this.name = 'SignificantChangeValidationError';
    }
}
// ---- classification ------------------------------------------------------------
/** The program change-notice findings count in: the first whose manifest has significant change notices (FedRAMP). */
const scnProgram = () => programsWhere((m) => m.features.significantChangeNotices)[0]?.key ?? null;
const QUESTION_IDS = new Set(SCN_QUESTIONS.map((q) => q.id));
function validateAnswers(answers) {
    if (typeof answers !== 'object' || answers === null || Array.isArray(answers)) {
        throw new SignificantChangeValidationError('answers must be an object of question id → boolean.');
    }
    const out = {};
    for (const [k, v] of Object.entries(answers)) {
        if (!QUESTION_IDS.has(k))
            throw new SignificantChangeValidationError(`Unknown SCN question id "${k}".`);
        if (typeof v !== 'boolean')
            throw new SignificantChangeValidationError(`Answer to "${k}" must be true or false.`);
        out[k] = v;
    }
    return out;
}
/**
 * SCN-CSO-EVA order. Unanswered questions are treated as "no" for triggers,
 * but routine requires an explicit "yes, this is regular" — so missing data
 * can only land a change in adaptive (which notifies), never routine.
 */
export function evaluateSignificantChange(answers) {
    const a = validateAnswers(answers);
    const yes = (effect) => SCN_QUESTIONS.filter((q) => q.effect === effect && a[q.id] === true);
    const rules = (qs) => [...new Set(qs.flatMap((q) => q.rule_ids))];
    const base = { question_set: SCN_QUESTION_SET_VERSION };
    const classChange = yes('certification_class_change');
    const trf = yes('transformative');
    const adp = yes('adaptive');
    const gate = yes('routine_gate');
    const disq = yes('routine_disqualifier');
    if (trf.length > 0) {
        return { ...base, classification: 'transformative', certification_class_change: classChange.length > 0, basis: trf.map((q) => q.id), rule_ids: rules(trf) };
    }
    if (gate.length > 0 && adp.length === 0 && disq.length === 0) {
        return { ...base, classification: 'routine', certification_class_change: classChange.length > 0, basis: gate.map((q) => q.id), rule_ids: rules(gate) };
    }
    // SCN-CSO-EVA: "If it is not, then it is an adaptive change".
    const why = [...adp, ...disq];
    return {
        ...base,
        classification: 'adaptive',
        certification_class_change: classChange.length > 0,
        basis: why.length ? why.map((q) => q.id) : ['default:not_routine_not_transformative'],
        rule_ids: [...new Set(['SCN-CSO-EVA', ...rules(why), 'SCN-ADP-NTF'])],
    };
}
export function classifySignificantChange(answers) {
    return evaluateSignificantChange(answers).classification;
}
async function computeNotices(content, ctx) {
    const prevByRule = new Map(ctx.previous.map((n) => [n.rule_id, n]));
    const anchors = {
        planned_start: ctx.planned_start,
        completed: ctx.completed_at,
        verified: ctx.verified_at,
    };
    const out = [];
    for (const rule of SCN_NOTICE_RULES.filter((r) => r.classification === ctx.classification)) {
        const tf = await lookupTimeframe(content, rule.rule_id, {
            path: ctx.path, certificationClass: ctx.certificationClass, pain: null, variant: null,
        });
        const prov = timeframeProvenance(rule.rule_id, tf);
        const sent_at = prevByRule.get(rule.rule_id)?.sent_at ?? null;
        const sent_by_user_id = prevByRule.get(rule.rule_id)?.sent_by_user_id ?? null;
        const anchorAt = anchors[rule.anchor];
        let due_at = null;
        if (tf && anchorAt) {
            const at = new Date(anchorAt);
            due_at = (rule.direction === 'before'
                ? subtractTimeframe(at, tf.timeframe_num, tf.timeframe_type)
                : addTimeframe(at, tf.timeframe_num, tf.timeframe_type)).toISOString();
        }
        let status;
        if (sent_at)
            status = 'sent';
        else if (!tf)
            status = 'blocked_rules_not_approved';
        else if (ctx.status === 'cancelled')
            status = 'cancelled';
        else
            status = due_at ? 'scheduled' : 'awaiting_anchor';
        out.push({
            rule_id: rule.rule_id,
            name: tf ? (await ruleName(content, tf.timeframe_id)) ?? rule.rule_id : rule.rule_id,
            anchor: rule.anchor,
            direction: rule.direction,
            force: tf?.force ?? null,
            timeframe_num: tf?.timeframe_num ?? null,
            timeframe_type: tf?.timeframe_type ?? null,
            rule_version: prov.rule_version,
            verification: prov.verification,
            calendar: prov.calendar,
            due_at,
            sent_at,
            sent_by_user_id,
            status,
        });
    }
    // A notice already SENT under an earlier class stays on the record (it reached the regulator).
    for (const prev of ctx.previous) {
        if (prev.sent_at && !out.some((n) => n.rule_id === prev.rule_id))
            out.push(prev);
    }
    return out;
}
async function ruleName(content, timeframeId) {
    const r = await content.query(`SELECT rule_name FROM certification_timeframes WHERE timeframe_id = ? LIMIT 1`, [timeframeId]);
    return r.results[0]?.rule_name ?? null;
}
async function profileScope(db, workspaceId, profileId) {
    if (!profileId)
        return { path: null, certificationClass: null };
    const p = await getProfile(db, workspaceId, profileId);
    if (!p)
        throw new SignificantChangeValidationError('profile_id must be a certification profile in this workspace.');
    return { path: p.path, certificationClass: p.certification_class };
}
// ---- persistence ---------------------------------------------------------------
function toView(row) {
    const { notices_json, classification_answers_json, ...rest } = row;
    let answers = {};
    try {
        const parsed = JSON.parse(classification_answers_json || '{}');
        answers = parsed.answers ?? {};
    }
    catch { /* keep empty */ }
    let evaluation;
    try {
        evaluation = evaluateSignificantChange(answers);
    }
    catch {
        evaluation = { classification: row.classification, certification_class_change: false, basis: [], rule_ids: [], question_set: SCN_QUESTION_SET_VERSION };
    }
    // Notices stored before V98-0-4 carry no provenance: they can't cite a
    // rule version, so they read as pending until the next recompute.
    const notices = JSON.parse(notices_json || '[]').map((n) => ({
        ...n, rule_version: n.rule_version ?? null, verification: n.verification ?? 'verification_pending', calendar: n.calendar ?? null,
        sent_by_user_id: n.sent_by_user_id ?? null,
    }));
    const { assessor_view_json, system_ids_json, provider_keys_json, notice_draft_json: _draft, ai_model_change, suggested_class, ...base } = rest;
    void _draft;
    return {
        ...base, answers, evaluation, notices,
        suggested_class: (suggested_class ?? evaluation.classification),
        decided_class: row.decided_class ?? null,
        ai_model_change: Number(ai_model_change) === 1,
        system_ids: jsonList(system_ids_json),
        provider_keys: jsonList(provider_keys_json),
        assessor_view: parseJson(assessor_view_json ?? null),
    };
}
function jsonList(v) {
    try {
        const a = JSON.parse(v || '[]');
        return Array.isArray(a) ? a.filter((x) => typeof x === 'string') : [];
    }
    catch {
        return [];
    }
}
function parseJson(v) {
    if (!v)
        return null;
    try {
        return JSON.parse(v);
    }
    catch {
        return null;
    }
}
/** Answers that by themselves describe an AI model / provider change. */
const AI_MODEL_QUESTIONS = ['adp_ai_model_existing_service', 'trf_new_ai_capability'];
function isoOrNull(v, field) {
    if (v == null || v === '')
        return null;
    const t = Date.parse(v);
    if (Number.isNaN(t))
        throw new SignificantChangeValidationError(`${field} must be an ISO date.`);
    return new Date(t).toISOString();
}
export async function createSignificantChange(db, input) {
    const title = (input.title ?? '').trim();
    if (!title || title.length > 300)
        throw new SignificantChangeValidationError('title is required (≤ 300 characters).');
    const evaluation = evaluateSignificantChange(input.answers);
    if (evaluation.certification_class_change) {
        throw new SignificantChangeValidationError('A FedRAMP Certification class change requires a new assessment and cannot be handled as a Significant Change Notification (SCN-CSO-EVA).');
    }
    const source = input.source ?? 'manual';
    if (!['manual', 'component_delta', 'cloud_drift'].includes(source))
        throw new SignificantChangeValidationError('Unknown source.');
    const profileId = input.profile_id ?? null;
    const scope = await profileScope(db, input.workspace_id, profileId);
    const plannedStart = isoOrNull(input.planned_start, 'planned_start');
    const nowIso = (input.now ?? new Date()).toISOString();
    const systemIds = await validateSystems(db, input.workspace_id, input.system_ids ?? []);
    const providerKeys = [...new Set((input.provider_keys ?? []).map((k) => normalizeProviderKey(String(k))).filter(Boolean))].sort();
    if (providerKeys.length > 50)
        throw new SignificantChangeValidationError('At most 50 providers per change.');
    const answers = validateAnswers(input.answers);
    const modelProviders = providerKeys.length
        ? (await listExternalServices(db, input.workspace_id)).filter((x) => providerKeys.includes(x.provider_key) && x.service_kind === 'model_api')
        : [];
    const aiModelChange = input.ai_model_change === true || AI_MODEL_QUESTIONS.some((q) => answers[q] === true) || modelProviders.length > 0;
    const notices = await computeNotices(input.contentDb ?? db, {
        classification: evaluation.classification, status: 'planned',
        planned_start: plannedStart, completed_at: null, verified_at: null,
        ...scope, previous: [],
    });
    // The computed class is the SUGGESTION; `classification` (what notices are computed from) starts as it, provisionally.
    await db.execute(`INSERT INTO significant_changes (
       change_id, workspace_id, profile_id, title, description, classification, classification_answers_json,
       status, planned_start, notices_json, source, source_ref, created_by_user_id, created_at, updated_at,
       suggested_class, ai_model_change, system_ids_json, provider_keys_json
     ) VALUES (?, ?, ?, ?, ?, ?, ?, 'planned', ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`, [
        input.change_id, input.workspace_id, profileId, title, input.description ?? '', evaluation.classification,
        JSON.stringify({ question_set: SCN_QUESTION_SET_VERSION, answers }),
        plannedStart, JSON.stringify(notices), source, input.source_ref ?? null, input.created_by_user_id, nowIso, nowIso,
        evaluation.classification, aiModelChange ? 1 : 0, JSON.stringify(systemIds), JSON.stringify(providerKeys),
    ]);
    return (await getSignificantChange(db, input.workspace_id, input.change_id));
}
export async function getSignificantChange(db, workspaceId, changeId) {
    const r = await db.query(`SELECT * FROM significant_changes WHERE change_id = ? AND workspace_id = ? LIMIT 1`, [changeId, workspaceId]);
    return r.results[0] ? toView(r.results[0]) : null;
}
export async function listSignificantChanges(db, workspaceId, opts = {}) {
    const where = ['workspace_id = ?'];
    const params = [workspaceId];
    if (opts.profileId) {
        where.push('profile_id = ?');
        params.push(opts.profileId);
    }
    if (opts.status) {
        where.push('status = ?');
        params.push(opts.status);
    }
    if (opts.since) {
        where.push('COALESCE(completed_at, created_at) >= ?');
        params.push(opts.since);
    }
    const r = await db.query(`SELECT * FROM significant_changes WHERE ${where.join(' AND ')} ORDER BY created_at DESC, change_id`, params);
    return r.results.map(toView);
}
const TRANSITIONS = {
    planned: ['in_progress', 'completed', 'cancelled'],
    in_progress: ['completed', 'cancelled'],
    completed: ['verified'],
    verified: [],
    cancelled: [],
};
export async function transitionSignificantChange(db, input) {
    const r = await db.query(`SELECT * FROM significant_changes WHERE change_id = ? AND workspace_id = ? LIMIT 1`, [input.change_id, input.workspace_id]);
    const row = r.results[0];
    if (!row)
        throw new SignificantChangeValidationError('Significant change not found.');
    if (!TRANSITIONS[row.status].includes(input.to)) {
        throw new SignificantChangeValidationError(`Cannot move a ${row.status} change to ${input.to}.`);
    }
    let cancelReason = null;
    if (input.to === 'cancelled') {
        cancelReason = (input.reason ?? '').trim();
        if (cancelReason.length < 10 || cancelReason.length > 2000)
            throw new SignificantChangeValidationError('Say why the change is cancelled (at least 10 characters).');
        if (!input.actor_user_id)
            throw new SignificantChangeValidationError('A cancellation records who cancelled it.');
    }
    const at = input.to === 'cancelled' ? (input.now ?? new Date()).toISOString()
        : isoOrNull(input.at instanceof Date ? input.at.toISOString() : input.at, 'at');
    const completed_at = input.to === 'completed' ? at : row.completed_at;
    const verified_at = input.to === 'verified' ? at : row.verified_at;
    const verified_by = input.to === 'verified' ? (input.actor_user_id ?? null) : row.verified_by ?? null;
    if (verified_at && completed_at && verified_at < completed_at) {
        throw new SignificantChangeValidationError('Verification cannot precede completion.');
    }
    const scope = await profileScope(db, row.workspace_id, row.profile_id);
    const notices = await computeNotices(input.contentDb ?? db, {
        classification: row.classification, status: input.to,
        planned_start: row.planned_start, completed_at, verified_at,
        ...scope, previous: JSON.parse(row.notices_json || '[]'),
    });
    await db.batch([
        {
            sql: `UPDATE significant_changes SET status = ?, completed_at = ?, verified_at = ?, verified_by = ?, notices_json = ?, updated_at = ?
             WHERE change_id = ? AND workspace_id = ? AND status = ?`,
            params: [input.to, completed_at, verified_at, verified_by, JSON.stringify(notices), at, row.change_id, row.workspace_id, row.status],
        },
        ...(input.to === 'cancelled' ? [sodEventStatement({
                workspace_id: row.workspace_id, transition: 'config', event: 'scn_cancelled', actor_user_id: input.actor_user_id,
                record_kind: 'scn', record_id: row.change_id, item_kind: 'scn', item_id: row.change_id,
                detail: { reason: cancelReason, from_status: row.status }, created_at: at,
            })] : []),
    ]);
    return (await getSignificantChange(db, row.workspace_id, row.change_id));
}
/** Alias matching the V96-3-6 task wording. */
export const transition = transitionSignificantChange;
/**
 * Low-level marker that a notice went out; sent notices never go overdue.
 * V98-5-3: NOT the product send path — it skips the decision, co-approval,
 * draft and confirm gates and records no sender. Routes call
 * `sendScnNotice`; this stays for imports of notices already sent outside
 * GAC Pro (and fixtures).
 */
export async function recordNoticeSent(db, input) {
    const view = await getSignificantChange(db, input.workspace_id, input.change_id);
    if (!view)
        throw new SignificantChangeValidationError('Significant change not found.');
    const at = isoOrNull(input.at instanceof Date ? input.at.toISOString() : input.at, 'at');
    const notice = view.notices.find((n) => n.rule_id === input.rule_id);
    if (!notice)
        throw new SignificantChangeValidationError(`${input.rule_id} is not a notice for this change.`);
    const notices = view.notices.map((n) => (n.rule_id === input.rule_id ? { ...n, sent_at: at, status: 'sent' } : n));
    await db.execute(`UPDATE significant_changes SET notices_json = ?, updated_at = ? WHERE change_id = ? AND workspace_id = ?`, [JSON.stringify(notices), at, input.change_id, input.workspace_id]);
    return (await getSignificantChange(db, input.workspace_id, input.change_id));
}
/** Notices whose due date has passed without being sent (cancelled changes excluded). */
export async function overdueNotices(db, workspaceId, now) {
    const r = await db.query(`SELECT * FROM significant_changes WHERE workspace_id = ? AND status != 'cancelled' ORDER BY created_at, change_id`, [workspaceId]);
    const nowIso = now.toISOString();
    const out = [];
    for (const row of r.results) {
        for (const n of JSON.parse(row.notices_json || '[]')) {
            if (!n.sent_at && n.due_at && n.due_at < nowIso && n.status === 'scheduled') {
                out.push({ ...n, change_id: row.change_id, title: row.title, classification: row.classification });
            }
        }
    }
    return out.sort((a, b) => (a.due_at < b.due_at ? -1 : a.due_at > b.due_at ? 1 : 0));
}
/**
 * Sweep: every overdue SCN notice → one `significant_change` finding
 * (dedup 'scn:<change>:<rule>'); a notice later marked sent auto-closes it.
 * Deadline = the notice's own due date (deadline_policy 'obligation_due').
 */
export async function syncScnNoticeFindings(db, now, newId) {
    const workspaces = await db.query(`SELECT DISTINCT workspace_id FROM significant_changes WHERE status != 'cancelled'`);
    let opened = 0;
    let closed = 0;
    const nowIso = now.toISOString();
    for (const { workspace_id } of workspaces.results) {
        const overdue = await overdueNotices(db, workspace_id, now);
        const keys = new Set();
        for (const n of overdue) {
            const dedup = `scn:${n.change_id}:${n.rule_id}`;
            keys.add(dedup);
            const r = await db.execute(`INSERT INTO findings (finding_id, workspace_id, control_id, kind, status, detail, dedup_key, severity, sla_hours,
                               sla_deadline_at, deadline_policy, certification_program, opened_at, updated_at)
         SELECT ?, ?, 'SEC-GOV-08', 'significant_change', 'open', ?, ?, 'high', 1, ?, 'obligation_due', ?, ?, ?
          WHERE NOT EXISTS (SELECT 1 FROM findings WHERE workspace_id = ? AND dedup_key = ? AND status IN ('open', 'acknowledged'))`, [newId(), workspace_id, JSON.stringify({ change: n.title, notice: n.rule_id, classification: n.classification, due_at: n.due_at }),
                dedup, n.due_at, scnProgram(), nowIso, nowIso, workspace_id, dedup]);
            opened += r.meta?.changes ?? 0;
        }
        // Only overdue-notice findings ('scn:<change>:<rule>') are reconciled here; the
        // transformative "engage the assessor" finding ('scn-trf:<change>') closes with its work item.
        const open = await db.query(`SELECT finding_id, dedup_key FROM findings WHERE workspace_id = ? AND kind = 'significant_change' AND status IN ('open', 'acknowledged')
          AND dedup_key LIKE 'scn:%'`, [workspace_id]);
        for (const f of open.results) {
            if (keys.has(f.dedup_key))
                continue;
            const r = await db.execute(`UPDATE findings SET status = 'auto_closed', closed_at = ?, updated_at = ? WHERE finding_id = ? AND workspace_id = ?`, [nowIso, nowIso, f.finding_id, workspace_id]);
            closed += r.meta?.changes ?? 0;
        }
        // V98-5-3: a transformative change's "engage the assessor" finding closes once its work item is done
        // (or cancelled because the class was re-decided before any notice went out).
        const trf = await db.query(`SELECT f.finding_id, w.status FROM findings f
         JOIN significant_changes c ON c.transformative_finding_id = f.finding_id AND c.workspace_id = f.workspace_id
         JOIN work_items w ON w.work_item_id = c.engage_work_item_id AND w.workspace_id = c.workspace_id
        WHERE f.workspace_id = ? AND f.status IN ('open', 'acknowledged') AND w.status <> 'open'`, [workspace_id]);
        for (const f of trf.results) {
            const r = await db.execute(`UPDATE findings SET status = 'auto_closed', closed_at = ?, updated_at = ? WHERE finding_id = ? AND workspace_id = ?`, [nowIso, nowIso, f.finding_id, workspace_id]);
            closed += r.meta?.changes ?? 0;
        }
    }
    return { opened, closed };
}
export class ScnStateError extends Error {
    code;
    detail;
    constructor(code, message, detail) {
        super(message);
        this.code = code;
        this.detail = detail;
        this.name = 'ScnStateError';
    }
}
export class SignificantChangeNotFoundError extends Error {
    constructor(id) {
        super(`Significant change not found: ${id}`);
        this.name = 'SignificantChangeNotFoundError';
    }
}
/** Every decision says why; departing from the suggestion says more. */
export const SCN_RATIONALE_MIN = 10;
export const SCN_OVERRIDE_RATIONALE_MIN = 20;
const SCN_CLASSES = ['routine', 'adaptive', 'transformative'];
async function loadRow(db, workspaceId, changeId) {
    const r = await db.query(`SELECT * FROM significant_changes WHERE change_id = ? AND workspace_id = ? LIMIT 1`, [changeId, workspaceId]);
    if (!r.results[0])
        throw new SignificantChangeNotFoundError(changeId);
    return r.results[0];
}
async function validateSystems(db, workspaceId, ids) {
    const want = [...new Set(ids.map(String))].sort();
    if (want.length > 100)
        throw new SignificantChangeValidationError('At most 100 systems per change.');
    if (!want.length)
        return [];
    const known = new Set((await listSystemFacts(db, workspaceId)).map((s) => s.system_id));
    const missing = want.filter((id) => !known.has(id));
    if (missing.length)
        throw new SignificantChangeValidationError('system_ids must be systems in this workspace.');
    return want;
}
/** D-98-13: an AI model / provider change decided short of transformative needs a second check. */
export function coApprovalRequired(v) {
    return v.ai_model_change && v.decided_class !== null && v.decided_class !== 'transformative';
}
/** Co-approval state, read from its signature request (one source of truth). */
export async function scnCoApproval(db, view) {
    const none = { request_id: null, approver_user_id: null, routing_status: null, signed_by_user_id: null, signed_at: null, reason: null };
    if (!coApprovalRequired(view))
        return { required: false, state: 'not_required', ...none };
    if (!view.co_approval_request_id)
        return { required: true, state: 'not_requested', ...none };
    const r = await getSignatureRequest(db, view.workspace_id, view.co_approval_request_id);
    if (!r)
        return { required: true, state: 'not_requested', ...none };
    const state = r.state === 'signed' ? 'approved' : r.state === 'requested' ? 'pending' : r.state === 'returned' ? 'returned' : 'not_requested';
    return {
        required: true, state, request_id: r.request_id, approver_user_id: r.approver_user_id, routing_status: r.routing_status,
        signed_by_user_id: r.signed_by_user_id, signed_at: r.signed_at, reason: r.state === 'returned' ? r.reason : null,
    };
}
/**
 * A person decides the class (D-98-13). The suggestion is never the
 * decision; departing from it needs a longer rationale. Once a notice went
 * out under a class, the class is fixed (the regulator has it). AI model /
 * provider changes decided short of transformative freeze a co-approval
 * request (kind 'scn') routed away from the classifier. A transformative
 * decision files the "engage the assessor" item + finding.
 */
export async function decideClassification(db, input) {
    const row = await loadRow(db, input.workspace_id, input.change_id);
    if (row.status === 'cancelled')
        throw new ScnStateError('cancelled', 'This change was cancelled.');
    if (!SCN_CLASSES.includes(input.decided_class))
        throw new SignificantChangeValidationError('decided_class must be routine, adaptive or transformative.');
    const view = toView(row);
    const rationale = (input.rationale ?? '').trim();
    const min = input.decided_class === view.suggested_class ? SCN_RATIONALE_MIN : SCN_OVERRIDE_RATIONALE_MIN;
    if (rationale.length < min || rationale.length > 4000) {
        throw new SignificantChangeValidationError(input.decided_class === view.suggested_class
            ? `Say why (at least ${SCN_RATIONALE_MIN} characters); it is recorded with your name and the time.`
            : `You are departing from the suggested class (${view.suggested_class}): say why in at least ${SCN_OVERRIDE_RATIONALE_MIN} characters.`);
    }
    const sentUnder = view.notices.find((n) => n.sent_at);
    if (sentUnder && input.decided_class !== row.classification) {
        throw new ScnStateError('already_sent', `A notice already went out as ${row.classification}; the class can no longer change. File a new change instead.`);
    }
    const required = coApprovalRequired({ ai_model_change: view.ai_model_change, decided_class: input.decided_class });
    if (required && !row.profile_id) {
        throw new SignificantChangeValidationError('An AI model or provider change needs a co-approver: file it under the FedRAMP program (profile_id) first.');
    }
    const now = input.now ?? new Date();
    const nowIso = now.toISOString();
    const scope = await profileScope(db, row.workspace_id, row.profile_id);
    const notices = await computeNotices(input.contentDb ?? db, {
        classification: input.decided_class, status: row.status,
        planned_start: row.planned_start, completed_at: row.completed_at, verified_at: row.verified_at,
        ...scope, previous: JSON.parse(row.notices_json || '[]'),
    });
    const res = await db.execute(`UPDATE significant_changes
        SET decided_class = ?, decided_by_user_id = ?, decided_at = ?, decision_rationale = ?, classification = ?, notices_json = ?, updated_at = ?
      WHERE change_id = ? AND workspace_id = ? AND updated_at = ?`, [input.decided_class, input.actor_user_id, nowIso, rationale, input.decided_class, JSON.stringify(notices), nowIso,
        row.change_id, row.workspace_id, row.updated_at]);
    if ((res.meta?.changes ?? 1) === 0)
        throw new ScnStateError('cancelled', 'The change was updated while you were deciding; reload it.');
    if (required) {
        // A fresh frozen version for every decision; an open earlier version becomes obsolete.
        const due = notices.filter((n) => !n.sent_at && n.due_at).map((n) => n.due_at).sort()[0] ?? null;
        const req = await createSignatureRequest(db, {
            workspace_id: row.workspace_id, artifact_kind: 'scn', artifact_id: row.change_id,
            title: `Co-approve change classification · ${row.title}`.slice(0, 300),
            content: {
                schema: 'gacpro/scn-classification@1', change_id: row.change_id, title: row.title, description: row.description,
                suggested_class: view.suggested_class, decided_class: input.decided_class, rationale,
                answers: view.answers, basis: view.evaluation.basis, rule_ids: view.evaluation.rule_ids, question_set: view.evaluation.question_set,
                ai_model_change: true, system_ids: view.system_ids, provider_keys: view.provider_keys,
            },
            preparer_user_id: input.actor_user_id, requested_by_user_id: input.actor_user_id,
            approver_subject: { kind: 'program', id: row.profile_id }, sign: input.sign, due_at: due, now,
        });
        await db.execute(`UPDATE significant_changes SET co_approval_request_id = ? WHERE change_id = ? AND workspace_id = ?`, [req.request_id, row.change_id, row.workspace_id]);
    }
    else if (row.co_approval_request_id) {
        await markObsoleteForArtifact(db, {
            workspace_id: row.workspace_id, artifact_kind: 'scn', artifact_id: row.change_id,
            reason: `Decided ${input.decided_class}: no co-approval needed`, actor_user_id: input.actor_user_id, now,
        });
    }
    if (input.decided_class === 'transformative') {
        await ensureTransformativeEngagement(db, (await loadRow(db, row.workspace_id, row.change_id)), input.actor_user_id, now);
    }
    else if (row.engage_work_item_id) {
        try {
            await closeWorkItem(db, { workspace_id: row.workspace_id, work_item_id: row.engage_work_item_id, status: 'cancelled',
                reason: `Re-decided ${input.decided_class}`, actor_user_id: input.actor_user_id, now });
        }
        catch { /* already closed */ }
        if (row.transformative_finding_id) {
            await db.execute(`UPDATE findings SET status = 'auto_closed', closed_at = ?, updated_at = ? WHERE finding_id = ? AND workspace_id = ? AND status IN ('open', 'acknowledged')`, [nowIso, nowIso, row.transformative_finding_id, row.workspace_id]);
        }
    }
    return (await getSignificantChange(db, row.workspace_id, row.change_id));
}
/** The program lead of the change's program, else the decider. */
async function engagementOwner(db, row, actor, now) {
    if (!row.profile_id)
        return actor;
    const iso = now.toISOString();
    // V98-7-5: assignments are tenant-plane, memberships control-plane — two queries, filtered here.
    const r = await db.query(`SELECT a.user_id FROM assignments a
      WHERE a.workspace_id = ? AND a.subject_kind = 'program' AND a.subject_id = ? AND a.responsibility = 'program_lead'
        AND a.effective_from <= ? AND (a.effective_to IS NULL OR a.effective_to > ?)
      ORDER BY a.effective_from ASC, a.user_id ASC`, [row.workspace_id, row.profile_id, iso, iso]);
    const members = await activeMemberIds(db, row.workspace_id);
    return r.results.find((a) => members.has(a.user_id))?.user_id ?? actor;
}
/**
 * Transformative branch (findings round 3): ONE "engage the assessor" work
 * item (owned by the program lead) and ONE `significant_change` finding
 * (dedup `scn-trf:<change>`), linked from the change. Idempotent.
 */
async function ensureTransformativeEngagement(db, row, actor, now) {
    const nowIso = now.toISOString();
    const notices = JSON.parse(row.notices_json || '[]');
    const earliest = notices.filter((n) => !n.sent_at && n.due_at).map((n) => n.due_at).sort()[0] ?? null;
    const due = earliest && earliest > nowIso ? earliest : addTimeframe(now, 5, 'bizdays').toISOString();
    let workItemId = row.engage_work_item_id;
    const open = workItemId
        ? (await db.query(`SELECT status FROM work_items WHERE work_item_id = ? AND workspace_id = ?`, [workItemId, row.workspace_id])).results[0]
        : undefined;
    if (!open || open.status === 'cancelled') {
        const owner = await engagementOwner(db, row, actor, now);
        try {
            const w = await createWorkItem(db, {
                workspace_id: row.workspace_id, kind: 'task', title: `Engage the assessor · ${row.title}`.slice(0, 300),
                detail: 'Decided transformative (SCN-TRF-TPR): engage your assessor about this change and plan its assessment before it starts. The notices before the change start come from the same decision.',
                due_at: due, source_kind: 'scn_engage_assessor', source_id: row.change_id, dedupe_key: `scn-engage:${row.change_id}`,
                owner_user_id: owner, consumers: [{ kind: 'scn', id: row.change_id }], actor_user_id: actor, now,
            });
            workItemId = w.work_item_id;
        }
        catch (err) {
            if (!(err instanceof WorkItemDuplicateError))
                throw err;
            workItemId = (await db.query(`SELECT work_item_id FROM work_items WHERE workspace_id = ? AND dedupe_key = ? AND status = 'open' LIMIT 1`, [row.workspace_id, `scn-engage:${row.change_id}`])).results[0]?.work_item_id ?? null;
        }
    }
    const dedup = `scn-trf:${row.change_id}`;
    const findingId = `fnd-${row.change_id}-trf`;
    await db.execute(`INSERT INTO findings (finding_id, workspace_id, control_id, kind, status, detail, dedup_key, severity, sla_hours,
                           sla_deadline_at, deadline_policy, certification_program, opened_at, updated_at)
     SELECT ?, ?, 'SEC-GOV-08', 'significant_change', 'open', ?, ?, 'high', ?, ?, 'obligation_due', ?, ?, ?
      WHERE NOT EXISTS (SELECT 1 FROM findings WHERE workspace_id = ? AND dedup_key = ? AND status IN ('open', 'acknowledged'))
        AND NOT EXISTS (SELECT 1 FROM findings WHERE finding_id = ?)`, [findingId, row.workspace_id,
        JSON.stringify({ change: row.title, classification: 'transformative', requires: 'assessor_engagement', rule: 'SCN-TRF-TPR', due_at: due }),
        dedup, Math.max(1, Math.round((Date.parse(due) - now.getTime()) / 3_600_000)), due, scnProgram(), nowIso, nowIso, row.workspace_id, dedup, findingId]);
    const f = (await db.query(`SELECT finding_id FROM findings WHERE workspace_id = ? AND dedup_key = ? AND status IN ('open', 'acknowledged') LIMIT 1`, [row.workspace_id, dedup])).results[0];
    await db.execute(`UPDATE significant_changes SET engage_work_item_id = ?, transformative_finding_id = ? WHERE change_id = ? AND workspace_id = ?`, [workItemId, f?.finding_id ?? row.transformative_finding_id, row.change_id, row.workspace_id]);
}
/**
 * The assessor's view of the class, as told to the provider — ADVISORY. It is
 * shown beside the decision and never satisfies the co-approval (D-98-13).
 */
export async function recordAssessorView(db, input) {
    const row = await loadRow(db, input.workspace_id, input.change_id);
    if (!SCN_CLASSES.includes(input.classification))
        throw new SignificantChangeValidationError('classification must be routine, adaptive or transformative.');
    const note = (input.note ?? '').trim();
    const assessor = (input.assessor_name ?? '').trim();
    if (!assessor || assessor.length > 200)
        throw new SignificantChangeValidationError('Name the assessor (≤ 200 characters).');
    if (note.length > 4000)
        throw new SignificantChangeValidationError('The note is at most 4000 characters.');
    const nowIso = (input.now ?? new Date()).toISOString();
    const viewJson = {
        classification: input.classification, note, assessor_name: assessor, recorded_by_user_id: input.actor_user_id, recorded_at: nowIso, advisory: true,
    };
    await db.execute(`UPDATE significant_changes SET assessor_view_json = ?, updated_at = ? WHERE change_id = ? AND workspace_id = ?`, [JSON.stringify(viewJson), nowIso, row.change_id, row.workspace_id]);
    return (await getSignificantChange(db, row.workspace_id, row.change_id));
}
async function boundaryFacts(db, view, now) {
    const facts = await listSystemFacts(db, view.workspace_id);
    const register = await listExternalServices(db, view.workspace_id, { now });
    const boundary = view.profile_id ? new Set(await currentBoundarySystemIds(db, view.workspace_id, view.profile_id)) : null;
    const agencies = view.profile_id ? (await db.query(`SELECT agency_name FROM cds_grants WHERE workspace_id = ? AND profile_id = ? AND status = 'active' AND expires_at > ? ORDER BY agency_name ASC`, [view.workspace_id, view.profile_id, now.toISOString()])).results.map((g) => g.agency_name) : [];
    return {
        systems: new Map(facts.map((f) => [f.system_id, { name: f.name, handles_federal_data: f.handles_federal_data, providers: f.providers }])),
        boundary,
        register: new Map(register.map((x) => [x.provider_key, { name: x.name, fedramp_status: x.fedramp_status, service_kind: x.service_kind }])),
        agencies: [...new Set(agencies)],
    };
}
function derivedDraft(view, f, now) {
    const touched = view.system_ids.map((id) => ({ id, s: f.systems.get(id) })).filter((x) => x.s);
    const used = new Set(touched.flatMap((x) => x.s.providers));
    return {
        schema: 'gacpro/scn-notice-draft@1',
        change_id: view.change_id,
        title: view.title,
        classification: view.decided_class,
        what_changed: (view.description || view.title).slice(0, 4000),
        systems: touched.map((x) => ({ system_id: x.id, name: x.s.name })),
        providers: view.provider_keys.map((k) => ({ provider_key: k, name: f.register.get(k)?.name ?? k, claim: f.register.has(k) ? 'listed' : 'new' })),
        data_flows: view.provider_keys.every((k) => used.has(k)) ? 'unchanged' : 'changed',
        federal_data: touched.some((x) => x.s.handles_federal_data === true) ? 'present' : 'none',
        recipients: ['FedRAMP', ...f.agencies],
        generated_at: now.toISOString(),
    };
}
/** Every way the draft disagrees with the boundary / register / decision. Pure. */
export function checkScnDraft(draft, view, f) {
    const out = [];
    for (const p of draft.providers) {
        const listed = f.register.get(p.provider_key);
        if (p.claim === 'listed' && !listed) {
            out.push({ code: 'provider_not_listed', field: `providers.${p.provider_key}`,
                message: `The draft says ${p.name} is a listed external service, but ${p.name} is not in the external-services register. Add it to the register (Scope) or say it is new.` });
        }
        if (p.claim === 'new' && listed) {
            out.push({ code: 'provider_already_listed', field: `providers.${p.provider_key}`,
                message: `The draft calls ${listed.name} new, but it is already in the external-services register.` });
        }
    }
    const inDraft = new Set(draft.providers.map((p) => p.provider_key));
    for (const k of view.provider_keys) {
        if (!inDraft.has(k))
            out.push({ code: 'provider_omitted', field: 'providers', message: `The change involves ${f.register.get(k)?.name ?? k}, but the draft leaves it out.` });
    }
    const draftSystems = new Set(draft.systems.map((s) => s.system_id));
    for (const id of view.system_ids) {
        if (!draftSystems.has(id))
            out.push({ code: 'system_omitted', field: 'systems', message: `The change touches ${f.systems.get(id)?.name ?? id}, but the draft leaves it out.` });
    }
    if (f.boundary) {
        for (const s of draft.systems) {
            if (!f.boundary.has(s.system_id)) {
                out.push({ code: 'system_not_in_boundary', field: 'systems', message: `${s.name} is not in this program's boundary. Add it to the boundary (Scope) before notifying about it.` });
            }
        }
    }
    const federal = view.system_ids.some((id) => f.systems.get(id)?.handles_federal_data === true);
    if (draft.federal_data === 'none' && federal) {
        out.push({ code: 'federal_data_understated', field: 'federal_data', message: 'The draft says no federal data is involved, but a system it touches handles federal data.' });
    }
    const used = new Set(view.system_ids.flatMap((id) => f.systems.get(id)?.providers ?? []));
    const unused = view.provider_keys.filter((k) => !used.has(k));
    if (draft.data_flows === 'unchanged' && unused.length) {
        out.push({ code: 'data_flow_changed', field: 'data_flows',
            message: `The draft says data flows are unchanged, but ${unused.map((k) => f.register.get(k)?.name ?? k).join(', ')} ${unused.length === 1 ? 'is' : 'are'} not a provider of the systems it touches yet.` });
    }
    if (!view.decided_class) {
        out.push({ code: 'not_decided', field: 'classification', message: 'The class is only suggested. Decide it before sending.' });
    }
    else if (draft.classification !== view.decided_class) {
        out.push({ code: 'class_mismatch', field: 'classification', message: `The draft says ${draft.classification ?? 'no class'}, but the decided class is ${view.decided_class}. Regenerate the draft.` });
    }
    if (!draft.recipients.includes('FedRAMP'))
        out.push({ code: 'recipient_missing', field: 'recipients', message: 'FedRAMP must receive every change notice.' });
    return out;
}
/** What the draft SAYS (the build stamp excluded), so re-reading the same draft confirms the same digest. */
const draftSha = (d) => sourceSha256(d);
async function draftView(db, view, stored, now) {
    const f = await boundaryFacts(db, view, now);
    const saved = parseJson(stored);
    const draft = saved ?? derivedDraft(view, f, now);
    return { draft, sha256: await draftSha(draft), contradictions: checkScnDraft(draft, view, f), edited: !!saved };
}
export async function getScnDraft(db, workspaceId, changeId, now = new Date()) {
    const row = await loadRow(db, workspaceId, changeId);
    return draftView(db, toView(row), row.notice_draft_json, now);
}
/** Edit the narrative and the claims; systems, providers, class and recipients stay derived. */
export async function saveScnDraft(db, input) {
    const now = input.now ?? new Date();
    const row = await loadRow(db, input.workspace_id, input.change_id);
    const view = toView(row);
    const f = await boundaryFacts(db, view, now);
    const p = input.patch ?? {};
    let draft;
    if (p.regenerate) {
        draft = derivedDraft(view, f, now);
    }
    else {
        const base = parseJson(row.notice_draft_json) ?? derivedDraft(view, f, now);
        draft = { ...base, generated_at: base.generated_at };
        if (p.what_changed !== undefined) {
            const t = String(p.what_changed).trim();
            if (!t || t.length > 4000)
                throw new SignificantChangeValidationError('What changed is required (≤ 4000 characters).');
            draft.what_changed = t;
        }
        if (p.provider_claims) {
            for (const [k, claim] of Object.entries(p.provider_claims)) {
                if (claim !== 'listed' && claim !== 'new')
                    throw new SignificantChangeValidationError('A provider claim is listed or new.');
                const hit = draft.providers.find((x) => x.provider_key === k);
                if (!hit)
                    throw new SignificantChangeValidationError(`${k} is not a provider of this change.`);
                hit.claim = claim;
            }
        }
        if (p.data_flows !== undefined) {
            if (p.data_flows !== 'unchanged' && p.data_flows !== 'changed')
                throw new SignificantChangeValidationError('data_flows is unchanged or changed.');
            draft.data_flows = p.data_flows;
        }
        if (p.federal_data !== undefined) {
            if (p.federal_data !== 'none' && p.federal_data !== 'present')
                throw new SignificantChangeValidationError('federal_data is none or present.');
            draft.federal_data = p.federal_data;
        }
    }
    await db.execute(`UPDATE significant_changes SET notice_draft_json = ?, updated_at = ? WHERE change_id = ? AND workspace_id = ?`, [JSON.stringify(draft), now.toISOString(), row.change_id, row.workspace_id]);
    return { draft, sha256: await draftSha(draft), contradictions: checkScnDraft(draft, view, f), edited: !p.regenerate };
}
function gateFor(view, notice, co, draft) {
    const blockers = [];
    if (view.status === 'cancelled')
        blockers.push({ code: 'cancelled', message: 'This change was cancelled.' });
    if (notice?.sent_at)
        blockers.push({ code: 'already_sent', message: 'This notice was already sent.' });
    if (notice && notice.status === 'awaiting_anchor') {
        blockers.push({ code: 'notice_not_due', message: notice.anchor === 'completed' ? 'Send once the change is completed.' : notice.anchor === 'verified' ? 'Send once the change is verified.' : 'Set the planned start first.' });
    }
    if (!view.decided_class)
        blockers.push({ code: 'not_decided', message: 'The class is only suggested. Decide it first.' });
    if (co.required && co.state !== 'approved') {
        blockers.push(co.state === 'returned'
            ? { code: 'co_approval_returned', message: `The co-approver sent the classification back${co.reason ? `: ${co.reason}` : ''}.` }
            : { code: 'co_approval_pending', message: 'An AI model or provider change needs its co-approval before the notice goes out.' });
    }
    if (draft.contradictions.length) {
        blockers.push({ code: 'draft_contradicts_boundary', message: `The draft contradicts the boundary in ${draft.contradictions.length} place${draft.contradictions.length === 1 ? '' : 's'}.` });
    }
    return { ready: blockers.length === 0, blockers };
}
/** V98-5 review T5-I2: how the sender delivered the notice outside GAC Pro (no delivery channel is in scope). */
export const SCN_DELIVERY_METHODS = ['email', 'portal', 'other'];
/**
 * The notice exactly as recorded and downloaded: the canonical draft without
 * its build stamp. sha256(scnNoticeJson(d)) === the draft's `sha256`, so the
 * bytes the sender downloads are the bytes they confirm and the record keeps.
 */
export function scnNoticeJson(draft) {
    return sourceCanonicalJson(draft);
}
function validateDelivery(d, now, createdAt) {
    if (!d || typeof d !== 'object')
        throw new SignificantChangeValidationError('Say how you sent it (email, portal or other) and the day it went out.');
    if (!SCN_DELIVERY_METHODS.includes(d.method))
        throw new SignificantChangeValidationError('How you sent it is email, portal or other.');
    if (typeof d.sent_on !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(d.sent_on) || Number.isNaN(Date.parse(`${d.sent_on}T00:00:00Z`))) {
        throw new SignificantChangeValidationError('The day it was sent is a date (YYYY-MM-DD).');
    }
    // A day ahead of UTC is still "today" somewhere; anything later is in the future.
    const latest = new Date(now.getTime() + 86_400_000).toISOString().slice(0, 10);
    if (d.sent_on > latest)
        throw new SignificantChangeValidationError('The day it was sent can’t be in the future.');
    if (d.sent_on < createdAt.slice(0, 10))
        throw new SignificantChangeValidationError('The day it was sent can’t be before the change was filed.');
    const note = (d.note ?? '').trim();
    if (note.length > 500)
        throw new SignificantChangeValidationError('The delivery note is at most 500 characters.');
    if (d.method === 'other' && note.length < 3)
        throw new SignificantChangeValidationError('Say how it was sent.');
    return { method: d.method, sent_on: d.sent_on, note };
}
/**
 * "Record as sent" (D-98-13; V98-5 review T5-I2). GAC Pro has no channel to
 * FedRAMP or the agencies: the sender delivers the downloaded notice and
 * records it here. Gates: decided class, co-approval when required, a draft
 * that agrees with the boundary, an explicit confirm of that exact draft, how
 * and when it was sent, and — when the due date is a placeholder — an
 * acknowledgement. Records who, when, how and the exact notice bytes + their
 * sha256 (append-only).
 */
export async function sendScnNotice(db, input) {
    const now = input.now ?? new Date();
    const row = await loadRow(db, input.workspace_id, input.change_id);
    const view = toView(row);
    const notice = view.notices.find((n) => n.rule_id === input.rule_id);
    if (!notice)
        throw new SignificantChangeValidationError(`${input.rule_id} is not a notice for this change.`);
    const co = await scnCoApproval(db, view);
    const dv = await draftView(db, view, row.notice_draft_json, now);
    const gate = gateFor(view, notice, co, dv);
    if (!gate.ready) {
        const first = gate.blockers[0];
        throw new ScnStateError(first.code, first.message, first.code === 'draft_contradicts_boundary' ? dv.contradictions : gate.blockers);
    }
    if (input.confirm !== true) {
        throw new ScnStateError('confirm_required', 'Confirm you are sending this exact draft.', { draft_sha256: dv.sha256, recipients: dv.draft.recipients });
    }
    if (input.draft_sha256 !== dv.sha256) {
        throw new ScnStateError('draft_changed', 'The draft changed since you read it. Read it again, then confirm.', { draft_sha256: dv.sha256 });
    }
    const delivery = validateDelivery(input.delivery, now, row.created_at);
    const pending = notice.verification === 'verification_pending';
    if (pending && input.verification_ack !== true) {
        throw new ScnStateError('verification_ack_required', 'The due date is a placeholder until the FedRAMP rules are verified. Confirm you checked the date yourself.');
    }
    const noticeJson = scnNoticeJson(dv.draft);
    const noticeSha = await sha256Hex(noticeJson);
    const nowIso = now.toISOString();
    const notices = view.notices.map((n) => (n.rule_id === input.rule_id
        ? { ...n, sent_at: nowIso, sent_by_user_id: input.actor_user_id, status: 'sent' } : n));
    try {
        await db.batch([
            {
                sql: `INSERT INTO scn_notice_sends (send_id, workspace_id, change_id, rule_id, classification, sent_by_user_id, confirmed_at, sent_at,
                                            draft_json, draft_sha256, recipients_json, co_approval_request_id,
                                            delivery_method, delivered_on, delivery_note, notice_json, notice_sha256, verification_acknowledged)
              VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
                params: [generateRandomId('scns'), row.workspace_id, row.change_id, input.rule_id, view.decided_class, input.actor_user_id, nowIso, nowIso,
                    canonicalJson(dv.draft), dv.sha256, JSON.stringify(dv.draft.recipients), co.required ? co.request_id : null,
                    delivery.method, delivery.sent_on, delivery.note || null, noticeJson, noticeSha, pending ? 1 : 0],
            },
            {
                sql: `UPDATE significant_changes SET notices_json = ?, updated_at = ? WHERE change_id = ? AND workspace_id = ?`,
                params: [JSON.stringify(notices), nowIso, row.change_id, row.workspace_id],
            },
        ]);
    }
    catch (err) {
        if (/unique|duplicate/i.test(String(err?.message ?? err)))
            throw new ScnStateError('already_sent', 'This notice was already sent.');
        throw err;
    }
    return (await getSignificantChange(db, row.workspace_id, row.change_id));
}
export async function listScnNoticeSends(db, workspaceId, changeId) {
    const r = await db.query(`SELECT send_id, rule_id, classification, sent_by_user_id, confirmed_at, sent_at, draft_sha256, recipients_json, co_approval_request_id,
            delivery_method, delivered_on, delivery_note, notice_sha256, verification_acknowledged
       FROM scn_notice_sends WHERE workspace_id = ? AND change_id = ? ORDER BY sent_at ASC, send_id ASC`, [workspaceId, changeId]);
    return r.results.map(({ recipients_json, verification_acknowledged, ...x }) => ({
        ...x, recipients: jsonList(recipients_json), verification_acknowledged: Number(verification_acknowledged ?? 0) === 1,
    }));
}
/**
 * The notice a sender downloads: once recorded, the bytes stored on the send
 * record; before that, the current draft's canonical bytes (whose sha256 is
 * the `draft_sha256` the confirm binds to).
 */
export async function getScnNoticeDocument(db, workspaceId, changeId, ruleId, now = new Date()) {
    const row = await loadRow(db, workspaceId, changeId);
    const view = toView(row);
    if (!view.notices.some((n) => n.rule_id === ruleId))
        throw new SignificantChangeValidationError(`${ruleId} is not a notice for this change.`);
    const filename = `change-notice-${changeId}-${ruleId}`.replace(/[^A-Za-z0-9._-]/g, '_');
    const sent = (await db.query(`SELECT notice_json, notice_sha256, draft_json FROM scn_notice_sends WHERE workspace_id = ? AND change_id = ? AND rule_id = ? LIMIT 1`, [workspaceId, changeId, ruleId])).results[0];
    if (sent) {
        // Rows recorded before migration 249 kept only the draft: its canonical notice is derived from it.
        const json = sent.notice_json ?? scnNoticeJson(JSON.parse(sent.draft_json));
        return { json, sha256: sent.notice_sha256 ?? await sha256Hex(json), recorded: true, filename };
    }
    const dv = await draftView(db, view, row.notice_draft_json, now);
    const json = scnNoticeJson(dv.draft);
    return { json, sha256: await sha256Hex(json), recorded: false, filename };
}
const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const CLASS_LABEL = { routine: 'Routine', adaptive: 'Adaptive', transformative: 'Transformative' };
/**
 * Printable notice (HTML) rendered from the exact JSON bytes, deterministic;
 * it cites their sha256 so a printout ties back to the record. Self-contained:
 * no scripts, no external assets (print to PDF from the browser).
 */
export function renderScnNoticeHtml(doc, meta) {
    const d = JSON.parse(doc.json);
    const row = (k, v) => `<tr><th scope="row">${esc(k)}</th><td>${esc(v)}</td></tr>`;
    return [
        '<!doctype html><html lang="en"><head><meta charset="utf-8"><title>', esc(`Change notice · ${d.title}`), '</title>',
        '<style>body{font:14px/1.5 system-ui,sans-serif;max-width:720px;margin:2rem auto;padding:0 1rem;color:CanvasText;background:Canvas}',
        'th{text-align:left;vertical-align:top;padding:.25rem 1rem .25rem 0;white-space:nowrap}td{padding:.25rem 0}footer{margin-top:2rem;font-size:12px}</style></head><body>',
        '<h1>', esc(`Significant change notice · ${d.title}`), '</h1>',
        '<table>',
        row('Notice', meta.notice_name),
        row('Class', d.classification ? CLASS_LABEL[d.classification] ?? d.classification : 'Not decided'),
        meta.due_at ? row('Due', meta.due_at.slice(0, 10)) : '',
        row('To', d.recipients.join(', ')),
        row('What changed', d.what_changed),
        row('Systems', d.systems.map((s) => s.name).join(', ') || 'None'),
        row('Providers', d.providers.map((p) => `${p.name} (${p.claim === 'listed' ? 'listed external service' : 'new provider'})`).join(', ') || 'None'),
        row('Data flows', d.data_flows === 'unchanged' ? 'Unchanged' : 'Changed'),
        row('Federal data', d.federal_data === 'present' ? 'Involved' : 'Not involved'),
        '</table>',
        '<footer>', esc(`Notice SHA-256 ${doc.sha256}`), '</footer>',
        '</body></html>',
    ].join('');
}
// ---- V98-5 review T5-B2: the transformative branch has a way forward ----------------------
/** The status moves the server allows from here (transition endpoint). */
export function allowedScnTransitions(status) {
    return TRANSITIONS[status];
}
/**
 * Set (or move) the planned start and recompute the notices anchored on it.
 * Refused once the change started (in progress or later) or once a notice
 * anchored on the planned start went out (the regulator has that date).
 */
export async function setScnPlannedStart(db, input) {
    const row = await loadRow(db, input.workspace_id, input.change_id);
    if (row.status !== 'planned')
        throw new SignificantChangeValidationError(`The change is ${row.status.replace('_', ' ')}; its planned start can no longer move.`);
    const planned = isoOrNull(input.planned_start, 'planned_start');
    if (!planned)
        throw new SignificantChangeValidationError('planned_start is required.');
    const view = toView(row);
    if (view.notices.some((n) => n.anchor === 'planned_start' && n.sent_at)) {
        throw new ScnStateError('planned_start_locked', 'A notice before the start already went out with this date; file a new change instead.');
    }
    const scope = await profileScope(db, row.workspace_id, row.profile_id);
    const notices = await computeNotices(input.contentDb ?? db, {
        classification: row.classification, status: row.status, planned_start: planned, completed_at: row.completed_at, verified_at: row.verified_at,
        ...scope, previous: JSON.parse(row.notices_json || '[]'),
    });
    const nowIso = (input.now ?? new Date()).toISOString();
    await db.execute(`UPDATE significant_changes SET planned_start = ?, notices_json = ?, updated_at = ? WHERE change_id = ? AND workspace_id = ? AND status = 'planned'`, [planned, JSON.stringify(notices), nowIso, row.change_id, row.workspace_id]);
    return (await getSignificantChange(db, row.workspace_id, row.change_id));
}
/** Everything the SCN item page shows — one read, every gate computed server-side. */
export async function getScnDetail(db, workspaceId, changeId, now = new Date()) {
    const row = await loadRow(db, workspaceId, changeId);
    const change = toView(row);
    const [co, draft, sends] = await Promise.all([scnCoApproval(db, change), draftView(db, change, row.notice_draft_json, now), listScnNoticeSends(db, workspaceId, changeId)]);
    const unsent = change.notices.filter((n) => !n.sent_at && n.status !== 'cancelled');
    const next = unsent.filter((n) => n.status !== 'awaiting_anchor').sort((a, b) => (a.due_at ?? '9999') < (b.due_at ?? '9999') ? -1 : 1)[0] ?? unsent[0] ?? null;
    return {
        change, co_approval: co, draft, sends, next_notice: next, send_gate: gateFor(change, next, co, draft),
        engagement: row.engage_work_item_id || row.transformative_finding_id ? { work_item_id: row.engage_work_item_id, finding_id: row.transformative_finding_id } : null,
        transitions: allowedScnTransitions(change.status),
        can_set_planned_start: change.status === 'planned' && !change.notices.some((n) => n.anchor === 'planned_start' && n.sent_at),
    };
}
/** Who may classify / edit the draft / send: owner or admin, the program lead, or the change's `owner`. */
export async function canOperateScn(db, workspaceId, change, user, now = new Date()) {
    if (user.role === 'owner' || user.role === 'admin')
        return true;
    if (change.profile_id && await hasAssignment(db, workspaceId, { kind: 'program', id: change.profile_id }, user.user_id, ['program_lead'], now))
        return true;
    return hasAssignment(db, workspaceId, { kind: 'scn', id: change.change_id }, user.user_id, ['owner'], now);
}
//# sourceMappingURL=significant-change-service.js.map